import React, { useEffect } from 'react'
import { FormikErrors, FormikProps } from 'formik'
import { ModalBody, ModalFooter } from 'uiComponents/popups/modal.new'
import { ActionButton } from 'uiComponents/buttons'
import classNames from 'classnames'
import { PricingPlan } from '../../types'
import { PricingPlanFormValues, TWO_YEAR_TERM, ONE_YEAR_TERM, getYearlyPrice } from '../../pricingPlanFormSchema'
import { PricingPlanElement } from '../../pricingPlan'
import { CheckboxFieldFormik, NumberInput, NumberInputProps } from 'uiComponents/input'
import { FormItem, WrapWithSymbol } from 'uiComponents/form/formElements'
import { useCurrency } from 'uiComponents/money'
import NumberInputFieldFormik, { NumberInputFormikProps } from 'uiComponents/input/numberInput/numberInputFieldFormik'
import SliderInput from 'uiComponents/input/slider/sliderInput'
import { CancelButton } from 'uiComponents/buttons/cancelButton'
import { RadioOptionTile } from 'uiComponents/optionTile'
import { useHasFeature } from 'features/hocs'
import './installments.scss'
import { useTranslation } from 'translations'

const MoneyInput = (props: NumberInputProps) => {
    const currencySymbol = useCurrency().symbol
    return (
        <WrapWithSymbol symbol={currencySymbol} position="left">
            <NumberInput {...props} />
        </WrapWithSymbol>
    )
}

const MoneyInputFormik = (props: NumberInputFormikProps) => {
    const currencySymbol = useCurrency().symbol
    return (
        <WrapWithSymbol symbol={currencySymbol} position="left">
            <NumberInputFieldFormik {...props} />
        </WrapWithSymbol>
    )
}

const getSuggestedPrice = (oldPrice: number) => oldPrice + Math.round(oldPrice / 10)

export const TWO_YEAR_PLANS_FEATURE = 'TwoYearMembershipPlans'

interface ContractLengthProps {
    minimumTerm: string
    onTermChange: (term: string) => void
}

const ContractLength = ({ minimumTerm, onTermChange }: ContractLengthProps) => {
    const { t } = useTranslation()
    return (
        <div className="contract-length">
            <label className="field-label">{t('products.pricing.pricingPlans.installments.contractLength')}</label>
            <div className="contract-length-options">
                <RadioOptionTile
                    title={t('products.pricing.pricingPlans.installments.oneYear')}
                    description={t('products.pricing.pricingPlans.installments.oneYearDescription')}
                    checked={minimumTerm === ONE_YEAR_TERM}
                    onClick={() => onTermChange(ONE_YEAR_TERM)}
                />
                <RadioOptionTile
                    title={t('products.pricing.pricingPlans.twoYears')}
                    description={t('products.pricing.pricingPlans.installments.twoYearsDescription')}
                    checked={minimumTerm === TWO_YEAR_TERM}
                    onClick={() => onTermChange(TWO_YEAR_TERM)}
                />
            </div>
        </div>
    )
}

interface AnnualPlanProps {
    allowYearlyInstallments: boolean
    suggestedPrice: number
}

const AnnualPlan = ({ allowYearlyInstallments, suggestedPrice }: AnnualPlanProps) => {
    const { t } = useTranslation()
    return (
        <div className="annual-plan">
            <label className="payment-plan-header" htmlFor="allowYearlyInstallments">
                <CheckboxFieldFormik name="allowYearlyInstallments" id="allowYearlyInstallments" />
                <span className="field-label">{t('products.pricing.pricingPlans.installments.annualPlan')}</span>
                <span>{t('products.pricing.pricingPlans.installments.annualPlanDescription')}</span>
            </label>
            <div
                className={classNames('collapsible', { expanded: allowYearlyInstallments })}
                {...({ inert: !allowYearlyInstallments || undefined } as any)}
            >
                <div className="collapsible-content">
                    <div className="annual-total-price-wrapper">
                        <label className="field-label">{t('products.pricing.pricingPlans.installments.totalPrice')}</label>
                        <FormItem className="annual-total-price">
                            <MoneyInputFormik
                                name="totalPriceYearly"
                                min={0}
                                maxLength={7}
                                placeholder={suggestedPrice.toFixed(2)}
                                autoCorrectInput={false}
                                resetToZero={false}
                            />
                        </FormItem>
                    </div>
                </div>
            </div>
        </div>
    )
}

interface PerYearPlanProps {
    allowPerYearPayment: boolean
    suggestedPrice: number
}

const PerYearPlan = ({ allowPerYearPayment, suggestedPrice }: PerYearPlanProps) => {
    const { t } = useTranslation()
    return (
        <div className="per-year-plan">
            <label className="payment-plan-header" htmlFor="allowPerYearPayment">
                <CheckboxFieldFormik name="allowPerYearPayment" id="allowPerYearPayment" />
                <span className="field-label">{t('products.pricing.pricingPlans.installments.payPerYear')}</span>
                <span>{t('products.pricing.pricingPlans.installments.payPerYearDescription')}</span>
            </label>
            <div
                className={classNames('collapsible', { expanded: allowPerYearPayment })}
                {...({ inert: !allowPerYearPayment || undefined } as any)}
            >
                <div className="collapsible-content">
                    <div className="annual-total-price-wrapper">
                        <label className="field-label">{t('products.pricing.pricingPlans.installments.pricePerYear')}</label>
                        <FormItem className="annual-total-price">
                            <MoneyInputFormik
                                name="pricePerYear"
                                min={0}
                                maxLength={7}
                                placeholder={suggestedPrice.toFixed(2)}
                                autoCorrectInput={false}
                                resetToZero={false}
                            />
                        </FormItem>
                    </div>
                </div>
            </div>
        </div>
    )
}

interface UpfrontPlanProps {
    allowUpfrontPayment: boolean
    suggestedPrice: number
}

const UpfrontPlan = ({ allowUpfrontPayment, suggestedPrice }: UpfrontPlanProps) => {
    const { t } = useTranslation()
    return (
        <div className="upfront-plan">
            <label className="payment-plan-header" htmlFor="allowUpfrontPayment">
                <CheckboxFieldFormik name="allowUpfrontPayment" id="allowUpfrontPayment" />
                <span className="field-label">{t('products.pricing.pricingPlans.installments.payUpfront')}</span>
                <span>{t('products.pricing.pricingPlans.installments.payUpfrontDescription')}</span>
            </label>
            <div
                className={classNames('collapsible', { expanded: allowUpfrontPayment })}
                {...({ inert: !allowUpfrontPayment || undefined } as any)}
            >
                <div className="collapsible-content">
                    <div className="annual-total-price-wrapper">
                        <label className="field-label">{t('products.pricing.pricingPlans.installments.totalPriceTwoYears')}</label>
                        <FormItem className="annual-total-price">
                            <MoneyInputFormik
                                name="priceUpfront"
                                min={0}
                                maxLength={7}
                                placeholder={suggestedPrice.toFixed(2)}
                                autoCorrectInput={false}
                                resetToZero={false}
                            />
                        </FormItem>
                    </div>
                </div>
            </div>
        </div>
    )
}

interface MonthlyPlanProps {
    allowMonthlyInstallments: boolean
    suggestedPrice: number
    totalPriceMonthly: number
    initialPayment: number
    averageMonthlyPrice: number
    monthlyInstallment: number
    hasValidTotalPriceMonthly: boolean
    onInitialPaymentChange: (value: number) => void
    onMonthlyInstallmentChange: (value: number) => void
    errors: FormikErrors<PricingPlanFormValues>
}

const MonthlyPlan = ({
    allowMonthlyInstallments,
    suggestedPrice,
    totalPriceMonthly,
    initialPayment,
    averageMonthlyPrice,
    monthlyInstallment,
    hasValidTotalPriceMonthly,
    onInitialPaymentChange,
    onMonthlyInstallmentChange,
    errors,
}: MonthlyPlanProps) => {
    const { t } = useTranslation()
    return (
        <div className="monthly-plan">
            <label className="payment-plan-header" htmlFor="allowMonthlyInstallments">
                <CheckboxFieldFormik name="allowMonthlyInstallments" id="allowMonthlyInstallments" />
                <span className="field-label">{t('products.pricing.pricingPlans.installments.monthlyPlan')}</span>
                <span>{t('products.pricing.pricingPlans.installments.monthlyPlanDescription')}</span>
            </label>
            <div
                className={classNames('collapsible', { expanded: allowMonthlyInstallments })}
                {...({ inert: !allowMonthlyInstallments || undefined } as any)}
            >
                <div className="collapsible-content">
                    <div className="monthly-total-price-wrapper">
                        <label className="field-label">{t('products.pricing.pricingPlans.installments.totalPrice')}</label>
                        <FormItem className="monthly-total-price">
                            <MoneyInputFormik
                                name="totalPriceMonthly"
                                min={0}
                                maxLength={7}
                                placeholder={suggestedPrice.toFixed(2)}
                                autoCorrectInput={false}
                                resetToZero={false}
                            />
                        </FormItem>
                    </div>
                    <div className="monthly-installments">
                        <div className="monthly-installments-row">
                            <label className="field-label">{t('products.pricing.pricingPlans.installments.initialPayment')}</label>
                            <div />
                            <label className="field-label">{t('products.pricing.pricingPlans.settings.perMonth')}</label>
                        </div>
                        <div className="monthly-installments-row">
                            <MoneyInputFormik
                                name="initialPayment"
                                maxLength={7}
                                min={averageMonthlyPrice}
                                max={totalPriceMonthly}
                                placeholder={(suggestedPrice / 12).toFixed(2)}
                                step={1}
                                resetToZero={false}
                            />
                            <div className="monthly-installment-slider">
                                <SliderInput
                                    name="initial-payment"
                                    min={-totalPriceMonthly + Math.min(11, averageMonthlyPrice)}
                                    max={-averageMonthlyPrice}
                                    step={5}
                                    restrictSteps={true}
                                    value={-initialPayment}
                                    onChange={(e, value) => onInitialPaymentChange(-Number(value))}
                                    disabled={!hasValidTotalPriceMonthly}
                                />
                            </div>
                            <MoneyInput
                                name="monthlyInstallment"
                                value={monthlyInstallment || ''}
                                maxLength={7}
                                min={0}
                                max={averageMonthlyPrice}
                                placeholder={(suggestedPrice / 12).toFixed(2)}
                                step={1}
                                resetToZero={false}
                                className={classNames({ error: errors.initialPayment })}
                                onChange={(e) => onMonthlyInstallmentChange(Number(e.target.value))}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

interface PricingPlanInstallmentsProps {
    formik: FormikProps<PricingPlanFormValues>
    visible: boolean
    activePricingPlan: PricingPlan | null
    onClose: () => void
    onNextClick: () => void
}

export const PricingPlanInstallments = ({
    formik,
    visible,
    activePricingPlan,
    onClose,
    onNextClick,
}: PricingPlanInstallmentsProps) => {
    const { t } = useTranslation()
    const oldYearlyPlan = activePricingPlan?.paymentPlans.find((plan) => plan.paymentInterval === 'P1Y')
    const oldMonthlyPlan = activePricingPlan?.paymentPlans.find((plan) => plan.paymentInterval === 'P1M')
    const oldUpfrontPlan = activePricingPlan?.paymentPlans.find((plan) => plan.paymentInterval === 'P2Y')

    const suggestedTotalPriceYearly = oldYearlyPlan ? getSuggestedPrice(getYearlyPrice(oldYearlyPlan)) : 200
    const suggestedTotalPriceMonthly = oldMonthlyPlan
        ? getSuggestedPrice(oldMonthlyPlan.totalPrice)
        : getSuggestedPrice(suggestedTotalPriceYearly) || 240

    const values = formik.values
    const isTwoYearTerm = values.minimumTerm === TWO_YEAR_TERM
    const hasTwoYearPlansFeature = useHasFeature(TWO_YEAR_PLANS_FEATURE)
    // Keep the selector visible on a plan that is already two-year, so it
    // stays editable even if the feature is later turned off for the account.
    const showContractLength = hasTwoYearPlansFeature || isTwoYearTerm

    const suggestedPriceUpfront = oldUpfrontPlan
        ? getSuggestedPrice(oldUpfrontPlan.totalPrice)
        : (values.pricePerYear || suggestedTotalPriceYearly) * 2

    const totalPriceMonthly = values.totalPriceMonthly ?? 0
    const initialPayment = values.initialPayment ?? 0
    const averageMonthlyPrice = Number((totalPriceMonthly / 12).toFixed(2))
    const monthlyInstallment =
        values.totalPriceMonthly != null && values.initialPayment != null
            ? Number(((totalPriceMonthly - initialPayment) / 11).toFixed(2))
            : averageMonthlyPrice

    const hasValidTotalPriceMonthly = totalPriceMonthly > 0

    const onTotalPriceYearlyChange = () => {
        formik.setFieldError('totalPriceYearly', undefined)
    }

    const onTotalPriceMonthlyChange = (newTotalPriceMonthly: number) => {
        formik.setFieldValue('totalPriceMonthly', newTotalPriceMonthly)
        formik.setFieldValue('initialPayment', Number((newTotalPriceMonthly / 12).toFixed(2)))
        formik.setFieldError('totalPriceMonthly', undefined)
        formik.setFieldError('initialPayment', undefined)
    }

    const onMonthlyInstallmentChange = (newMonthlyPrice: number) => {
        const newInitialPayment = Math.max(totalPriceMonthly - newMonthlyPrice * 11, newMonthlyPrice).toFixed(2)
        formik.setFieldValue('initialPayment', newInitialPayment || null)
        formik.setFieldError('initialPayment', undefined)
    }

    useEffect(onTotalPriceYearlyChange, [values.totalPriceYearly])

    useEffect(() => {
        formik.setFieldError('pricePerYear', undefined)
    }, [values.pricePerYear])

    useEffect(() => {
        formik.setFieldError('priceUpfront', undefined)
    }, [values.priceUpfront])

    useEffect(() => {
        if (values.totalPriceMonthly != null) {
            onTotalPriceMonthlyChange(totalPriceMonthly)
        }
    }, [totalPriceMonthly])

    const canGoNext = isTwoYearTerm
        ? values.allowPerYearPayment || values.allowUpfrontPayment
        : values.allowYearlyInstallments || values.allowMonthlyInstallments

    return (
        <div className={classNames('form-step pricing-plan-installments', { visible })}>
            <ModalBody>
                {activePricingPlan && (
                    <div className="current-plan-reference">
                        <label className="reference-header">{t('products.pricing.pricingPlans.installments.currentActivePlan')}</label>
                        <PricingPlanElement pricingPlan={activePricingPlan} readOnly />
                    </div>
                )}
                {showContractLength && (
                    <ContractLength
                        minimumTerm={values.minimumTerm}
                        onTermChange={(term) => formik.setFieldValue('minimumTerm', term)}
                    />
                )}
                <div
                    className={classNames('collapsible term-options', { expanded: !isTwoYearTerm })}
                    {...({ inert: isTwoYearTerm || undefined } as any)}
                >
                    <div className="collapsible-content">
                        <AnnualPlan
                            allowYearlyInstallments={values.allowYearlyInstallments}
                            suggestedPrice={suggestedTotalPriceYearly}
                        />
                        <MonthlyPlan
                            allowMonthlyInstallments={values.allowMonthlyInstallments}
                            suggestedPrice={suggestedTotalPriceMonthly}
                            totalPriceMonthly={totalPriceMonthly}
                            averageMonthlyPrice={averageMonthlyPrice}
                            initialPayment={initialPayment}
                            monthlyInstallment={monthlyInstallment}
                            hasValidTotalPriceMonthly={hasValidTotalPriceMonthly}
                            onInitialPaymentChange={(value) => formik.setFieldValue('initialPayment', value)}
                            onMonthlyInstallmentChange={onMonthlyInstallmentChange}
                            errors={formik.errors}
                        />
                    </div>
                </div>
                <div
                    className={classNames('collapsible term-options', { expanded: isTwoYearTerm })}
                    {...({ inert: !isTwoYearTerm || undefined } as any)}
                >
                    <div className="collapsible-content">
                        <PerYearPlan
                            allowPerYearPayment={values.allowPerYearPayment}
                            suggestedPrice={suggestedTotalPriceYearly}
                        />
                        <UpfrontPlan
                            allowUpfrontPayment={values.allowUpfrontPayment}
                            suggestedPrice={suggestedPriceUpfront}
                        />
                    </div>
                </div>
            </ModalBody>
            <ModalFooter>
                <CancelButton secondary size="large" onClick={onClose}>
                    {t('common.close')}
                </CancelButton>
                <ActionButton size="large" onClick={onNextClick} disabled={!canGoNext}>
                    {t('common.next')}
                </ActionButton>
            </ModalFooter>
        </div>
    )
}
